import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import type { SurveyQuestionDef } from '@/constants/playerSurvey';
import type { PlayerSurveyAnswerKey } from '@/constants/playerSurvey';
import type { PlayerSurveyAnswers } from '@/models/playerSurvey';
import { isSurveyQuestionVisible } from '@/models/playerSurvey';

interface SurveyQuestionFieldProps {
  question: SurveyQuestionDef;
  answers: PlayerSurveyAnswers;
  onChange: (key: PlayerSurveyAnswerKey, value: string | string[] | number | undefined) => void;
}

const optionStackProps = {
  spacing: 1.5,
  align: 'stretch' as const,
};

export function SurveyQuestionField({ question, answers, onChange }: SurveyQuestionFieldProps) {
  if (!isSurveyQuestionVisible(question, answers)) return null;

  const labelId = `survey-${question.id}`;

  if (question.kind === 'radio') {
    const value = typeof answers[question.id] === 'string' ? (answers[question.id] as string) : '';
    return (
      <FormControl textAlign='left' flexShrink={0}>
        <FormLabel id={labelId} fontWeight='semibold' fontSize='sm' mb={1.5} textAlign='left'>
          {question.prompt}
        </FormLabel>
        {question.helperText ? (
          <FormHelperText mt={0} mb={1.5} fontSize='xs' textAlign='left'>
            {question.helperText}
          </FormHelperText>
        ) : null}
        <RadioGroup
          value={value}
          onChange={(next) => onChange(question.id, next || undefined)}
          aria-labelledby={labelId}
        >
          <Stack {...optionStackProps}>
            {(question.options ?? []).map((option) => (
              <Radio key={option} value={option} size='sm' py={0.5}>
                {option}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>
    );
  }

  if (question.kind === 'checkbox') {
    const selected = Array.isArray(answers[question.id]) ? (answers[question.id] as string[]) : [];
    return (
      <FormControl textAlign='left' flexShrink={0}>
        <FormLabel id={labelId} fontWeight='semibold' fontSize='sm' mb={1.5} textAlign='left'>
          {question.prompt}
        </FormLabel>
        <CheckboxGroup
          value={selected}
          onChange={(next) => {
            const list = (next as string[]).slice();
            onChange(question.id, list.length > 0 ? list : undefined);
          }}
        >
          <Stack {...optionStackProps}>
            {(question.options ?? []).map((option) => (
              <Checkbox key={option} value={option} size='sm' py={0.5}>
                {option}
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      </FormControl>
    );
  }

  if (question.kind === 'nps') {
    const value = typeof answers[question.id] === 'number' ? String(answers[question.id]) : '';
    return (
      <FormControl textAlign='left' flexShrink={0}>
        <FormLabel id={labelId} fontWeight='semibold' fontSize='sm' mb={1.5} textAlign='left'>
          {question.prompt}
        </FormLabel>
        <RadioGroup
          value={value}
          onChange={(next) => {
            if (next === '') {
              onChange(question.id, undefined);
              return;
            }
            onChange(question.id, Number(next));
          }}
          aria-labelledby={labelId}
        >
          <HStack justify='space-between' mb={1} px={0.5}>
            <Text fontSize='xs' color='gray.500' textAlign='left'>
              0 — Not at all likely
            </Text>
            <Text fontSize='xs' color='gray.500' textAlign='right'>
              10 — Extremely likely
            </Text>
          </HStack>
          <Wrap spacing={2} justify='flex-start'>
            {Array.from({ length: 11 }, (_, n) => (
              <WrapItem key={n}>
                <Radio value={String(n)} size='sm'>
                  {n}
                </Radio>
              </WrapItem>
            ))}
          </Wrap>
        </RadioGroup>
      </FormControl>
    );
  }

  if (question.kind === 'shortText') {
    const value = typeof answers[question.id] === 'string' ? (answers[question.id] as string) : '';
    return (
      <FormControl textAlign='left' flexShrink={0}>
        <FormLabel htmlFor={labelId} fontWeight='semibold' fontSize='sm' mb={1.5} textAlign='left'>
          {question.prompt}
        </FormLabel>
        <Input
          id={labelId}
          size='md'
          fontSize='16px'
          value={value}
          onChange={(e) => onChange(question.id, e.target.value || undefined)}
          maxLength={300}
        />
      </FormControl>
    );
  }

  const value = typeof answers[question.id] === 'string' ? (answers[question.id] as string) : '';
  return (
    <FormControl textAlign='left' flexShrink={0}>
      <FormLabel htmlFor={labelId} fontWeight='semibold' fontSize='sm' mb={1.5} textAlign='left'>
        {question.prompt}
      </FormLabel>
      <Textarea
        id={labelId}
        size='md'
        fontSize='16px'
        value={value}
        onChange={(e) => onChange(question.id, e.target.value || undefined)}
        maxLength={4000}
        minH='120px'
        placeholder='Optional — praise, gripes, ideas, or anything we missed.'
      />
    </FormControl>
  );
}

export function SurveyProgressBar(props: { pageIndex: number; pageCount: number }) {
  const { pageIndex, pageCount } = props;
  const pct = Math.round((pageIndex / pageCount) * 100);
  return (
    <Box w='full' textAlign='left' flexShrink={0}>
      <Text fontSize='sm' color='gray.500' mb={1}>
        Page {pageIndex} of {pageCount}
      </Text>
      <Box w='full' h='8px' bg='surfaceInset' borderRadius='full' overflow='hidden'>
        <Box h='full' w={`${pct}%`} bg='brandAccent' borderRadius='full' transition='width 0.2s ease' />
      </Box>
    </Box>
  );
}
